import { normalizePeoplePage, normalizeWriteResult } from '../utils/normalize';
import { asUserId } from '../types/domain.types';

import type {
  ChangeGrantInput,
  ChangeRoleInput,
  CreatePersonInput,
  CreatePersonResult,
  GrantExceptionInput,
  InviteSent,
  InviteState,
  OverrideChange,
  PeopleFilters,
  PersonListPage,
  PlantAccessInput,
  ReasonInput,
  WriteResult,
} from '../types/domain.types';

import { USER_CENTER_ENDPOINTS } from './endpoints';
import { ApiError, request } from './http';
import {
  accessPreviewSchema,
  assignableSchema,
  createPersonResultSchema,
  inviteReadSchema,
  inviteSentSchema,
  overridesDryRunSchema,
  parseOrThrow,
  peoplePageSchema,
  personAccessSchema,
  writeResultSchema,
} from './schemas';
import type { AccessPreviewDto, AssignableDto, OverridesDryRunDto, PersonAccessDto } from './schemas';

export const fetchPeople = async (filters: PeopleFilters, signal?: AbortSignal): Promise<PersonListPage> => {
  const params: Record<string, unknown> = {};
  if (filters.q) params.q = filters.q;
  if (filters.plantId) params.plantId = filters.plantId;
  if (filters.roleKey) params.roleKey = filters.roleKey;
  if (filters.adminGrant) params.adminGrant = filters.adminGrant;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  const raw = await request('get', USER_CENTER_ENDPOINTS.PEOPLE, { params, signal });
  return normalizePeoplePage(parseOrThrow(peoplePageSchema, raw, 'people list'));
};

export const fetchPerson = async (userId: string, signal?: AbortSignal): Promise<PersonAccessDto> =>
  parseOrThrow(personAccessSchema, await request('get', USER_CENTER_ENDPOINTS.PERSON(userId), { signal }), 'person access');

export const fetchAccessPreview = async (userId: string, plantId: string, signal?: AbortSignal): Promise<AccessPreviewDto> =>
  parseOrThrow(
    accessPreviewSchema,
    await request('get', USER_CENTER_ENDPOINTS.PREVIEW(userId), { params: { plantId }, signal }),
    'access preview',
  );

export const fetchAssignable = async (signal?: AbortSignal): Promise<AssignableDto> =>
  parseOrThrow(assignableSchema, await request('get', USER_CENTER_ENDPOINTS.ASSIGNABLE, { signal }), 'assignable roles and grants');

export const createPerson = async (input: CreatePersonInput): Promise<CreatePersonResult> => {
  const raw = await request('post', USER_CENTER_ENDPOINTS.PEOPLE, {
    body: {
      name: input.name,
      email: input.email,
      ...(input.number ? { number: input.number } : {}),
      ...(input.title ? { title: input.title } : {}),
      ...(input.roleKey ? { roleKey: input.roleKey } : {}),
      ...(input.adminGrant ? { adminGrant: input.adminGrant } : {}),
      ...(input.clusterId ? { clusterId: input.clusterId } : {}),
      ...(input.plantIds && input.plantIds.length ? { plantIds: [...input.plantIds] } : {}),
      reason: input.reason,
    },
  });
  const { userId, invite, inviteError } = parseOrThrow(createPersonResultSchema, raw, 'create person');
  return {
    userId: asUserId(userId),
    invite: invite
      ? { sentTo: invite.sentTo, state: { sentAt: invite.sentAt, expiresAt: invite.expiresAt, expired: invite.expired, canResendAt: invite.canResendAt } }
      : null,
    inviteError: inviteError ?? null,
  };
};

export const fetchInvite = async (userId: string, signal?: AbortSignal): Promise<InviteState | null> =>
  parseOrThrow(inviteReadSchema, await request('get', USER_CENTER_ENDPOINTS.INVITE(userId), { signal }), 'invite').invite;

export const sendInvite = async (userId: string): Promise<InviteSent> => {
  const raw = await request('post', USER_CENTER_ENDPOINTS.INVITE(userId), {});
  const { sentTo, invite } = parseOrThrow(inviteSentSchema, raw, 'invite');
  return { sentTo, invite };
};

const write = async (method: 'put' | 'patch' | 'post' | 'delete', url: string, body: Record<string, unknown>): Promise<WriteResult> =>
  normalizeWriteResult(parseOrThrow(writeResultSchema, await request(method, url, { body }), 'write result'));

export const changeRole = (userId: string, input: ChangeRoleInput): Promise<WriteResult> =>
  write('put', USER_CENTER_ENDPOINTS.ROLE(userId), { roleKey: input.roleKey, reason: input.reason });

export const changeGrant = (userId: string, input: ChangeGrantInput): Promise<WriteResult> =>
  write('patch', USER_CENTER_ENDPOINTS.GRANT(userId), {
    grant: input.grant,
    ...(input.clusterId !== undefined ? { clusterId: input.clusterId } : {}),
    reason: input.reason,
  });

export const addPlantAccess = (userId: string, input: PlantAccessInput): Promise<WriteResult> =>
  write('post', USER_CENTER_ENDPOINTS.PLANTS(userId), { plantId: input.plantId, reason: input.reason });

export const removePlantAccess = (userId: string, plantId: string, input: ReasonInput): Promise<WriteResult> =>
  write('delete', USER_CENTER_ENDPOINTS.PLANT(userId, plantId), { reason: input.reason });

export const grantException = (userId: string, input: GrantExceptionInput): Promise<WriteResult> =>
  write('post', USER_CENTER_ENDPOINTS.EXCEPTIONS(userId), {
    plantId: input.plantId,
    exceptionKey: input.exceptionKey,
    reason: input.reason,
    ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
  });

export const revokeException = (userId: string, grantId: string, input: ReasonInput): Promise<WriteResult> =>
  write('delete', USER_CENTER_ENDPOINTS.EXCEPTION(userId, grantId), { reason: input.reason });

const changesBody = (changes: readonly OverrideChange[]) => changes.map((c) => ({ permissionKey: c.permissionKey, effect: c.effect }));

export const saveOverridesBatch = (
  userId: string,
  input: { plantId: string; changes: readonly OverrideChange[]; reason: string },
): Promise<WriteResult> =>
  write('post', USER_CENTER_ENDPOINTS.OVERRIDES_BATCH(userId), {
    plantId: input.plantId,
    changes: changesBody(input.changes),
    reason: input.reason,
  });

const refusedDryRun = (error: unknown): OverridesDryRunDto | null => {
  if (!(error instanceof ApiError) || error.status !== 422 || error.violations.length === 0) return null;
  return {
    applied: [],
    cascaded: [],
    violations: error.violations.map((v) => ({ code: v.code, message: v.message ?? null, keys: v.keys ?? [] })),
  };
};

export const dryRunOverrides = async (
  userId: string,
  input: { plantId: string; changes: readonly OverrideChange[] },
  signal?: AbortSignal,
): Promise<OverridesDryRunDto> => {
  try {
    const raw = await request('post', USER_CENTER_ENDPOINTS.OVERRIDES_BATCH(userId), {
      params: { dryRun: 1 },
      body: { plantId: input.plantId, changes: changesBody(input.changes) },
      signal,
    });
    return parseOrThrow(overridesDryRunSchema, raw, 'override dry run');
  } catch (error) {
    const refused = refusedDryRun(error);
    if (refused === null) throw error;
    return refused;
  }
};
